'use client'

import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import type { FontFamily, FontSize } from '@/lib/a11y/types'

import { useAccessibility } from '../AccessibilityProvider'

const FONT_SIZE_OPTIONS: { value: FontSize; label: string }[] = [
  { value: 'sm', label: 'Mala' },
  { value: 'md', label: 'Srednja' },
  { value: 'lg', label: 'Velika' },
  { value: 'xl', label: 'Veća' },
  { value: 'xxl', label: 'Najveća' },
]

const FONT_FAMILY_OPTIONS: { value: FontFamily; label: string; description: string }[] = [
  { value: 'lexend', label: 'Lexend', description: 'Preporučeno za lako čitanje' },
  { value: 'dyslexic', label: 'OpenDyslexic', description: 'Font prilagođen disleksiji' },
  { value: 'system', label: 'Sistemski', description: 'Podrazumevani font uređaja' },
]

export function TextSection() {
  const { settings, update, announce } = useAccessibility()

  return (
    <section aria-labelledby="a11y-text-heading" className="flex flex-col gap-4">
      <h3 id="a11y-text-heading" className="text-base font-semibold">
        Tekst
      </h3>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">Veličina fonta</legend>
        <RadioGroup
          value={settings.fontSize}
          onValueChange={(val) => {
            update({ fontSize: val as FontSize })
            announce(`Veličina fonta: ${FONT_SIZE_OPTIONS.find((o) => o.value === val)?.label}`)
          }}
          className="grid grid-cols-5 gap-2"
        >
          {FONT_SIZE_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem value={opt.value} id={`font-size-${opt.value}`} />
              <Label htmlFor={`font-size-${opt.value}`} className="text-xs">
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">Tip fonta</legend>
        <RadioGroup
          value={settings.fontFamily}
          onValueChange={(val) => {
            update({ fontFamily: val as FontFamily })
            announce(`Font: ${FONT_FAMILY_OPTIONS.find((o) => o.value === val)?.label}`)
          }}
          className="flex flex-col gap-2"
        >
          {FONT_FAMILY_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-start gap-2">
              <RadioGroupItem value={opt.value} id={`font-family-${opt.value}`} className="mt-1" />
              <Label htmlFor={`font-family-${opt.value}`} className="flex flex-col gap-0.5">
                <span>{opt.label}</span>
                <span className="text-muted-foreground text-xs font-normal">{opt.description}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="letter-spacing" className="flex-1 cursor-pointer">
            Povećan razmak između slova
          </Label>
          <Switch
            id="letter-spacing"
            checked={settings.letterSpacing === 'wide'}
            onCheckedChange={(checked) => {
              update({ letterSpacing: checked ? 'wide' : 'normal' })
              announce(`Razmak slova: ${checked ? 'povećan' : 'normalan'}`)
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="word-spacing" className="flex-1 cursor-pointer">
            Povećan razmak između reči
          </Label>
          <Switch
            id="word-spacing"
            checked={settings.wordSpacing === 'wide'}
            onCheckedChange={(checked) => {
              update({ wordSpacing: checked ? 'wide' : 'normal' })
              announce(`Razmak reči: ${checked ? 'povećan' : 'normalan'}`)
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="line-height" className="flex-1 cursor-pointer">
            Povećan razmak između linija
          </Label>
          <Switch
            id="line-height"
            checked={settings.lineHeight === 'wide'}
            onCheckedChange={(checked) => {
              update({ lineHeight: checked ? 'wide' : 'normal' })
              announce(`Razmak linija: ${checked ? 'povećan' : 'normalan'}`)
            }}
          />
        </div>
      </div>
    </section>
  )
}
